/**
 * XIV Dye Tools v2.0.0 - Application Constants
 *
 * Phase 12: Architecture Refactor
 * Centralized configuration and constant values
 *
 * @module shared/constants
 */

import type { ThemeName } from './types';
import type { LocaleCode, LocaleDisplay } from './i18n-types';
import { BASE_APP_NAME, BETA_TITLE_PREFIX } from './beta-branding';
import { SUPPORTED_LOCALES as CORE_SUPPORTED_LOCALES } from '@xivdyetools/core';

// ============================================================================
// Application Metadata
// ============================================================================

/**
 * Build environment, injected by Vite's `define`. `'beta'` for builds published
 * to beta.xivdyetools.app; absent everywhere else — including under Vitest,
 * which has no `define` block — hence the `typeof` guard.
 */
declare const __APP_ENV__: string;
export const APP_ENV = typeof __APP_ENV__ !== 'undefined' ? __APP_ENV__ : 'production';

/**
 * Product name for a given build environment. Anything that is not explicitly
 * `'beta'` is treated as production: an unrecognised value must not invent a
 * new marker.
 */
export function resolveAppName(env: string): string {
  return env === 'beta' ? `${BETA_TITLE_PREFIX}${BASE_APP_NAME}` : BASE_APP_NAME;
}

export const APP_NAME = resolveAppName(APP_ENV);
// Version injected from package.json by Vite at build time
declare const __APP_VERSION__: string;
export const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
// Build date (yyyy.mm.dd) stamped by Vite at build time — About's BUILD cell
declare const __BUILD_DATE__: string;
export const BUILD_DATE = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : '';
// ============================================================================
// Theme Configuration
// ============================================================================

/** 5.0 fixed decision: Light and Dark only */
export const THEME_NAMES: readonly ThemeName[] = ['standard-light', 'standard-dark'] as const;

export const DEFAULT_THEME: ThemeName = 'standard-dark';

// ============================================================================
// Localization Configuration
// ============================================================================

/**
 * Supported locale codes — derived from @xivdyetools/core's own
 * `SUPPORTED_LOCALES` (DEAD-037 Wave 4a) so the two cannot drift; a parity
 * test in `__tests__/constants.test.ts` also asserts `LOCALE_DISPLAY_INFO`'s
 * codes match this list 1:1.
 */
export const SUPPORTED_LOCALES: readonly LocaleCode[] = CORE_SUPPORTED_LOCALES;

export const DEFAULT_LOCALE: LocaleCode = 'en';

/**
 * Locale display information with flags and native names
 */
export const LOCALE_DISPLAY_INFO: readonly LocaleDisplay[] = [
  { code: 'en', name: 'English', englishName: 'English', flag: '🇬🇧' },
  { code: 'ja', name: '日本語', englishName: 'Japanese', flag: '🇯🇵' },
  { code: 'de', name: 'Deutsch', englishName: 'German', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', englishName: 'French', flag: '🇫🇷' },
  { code: 'ko', name: '한국어', englishName: 'Korean', flag: '🇰🇷' },
  { code: 'zh', name: '中文', englishName: 'Chinese', flag: '🇨🇳' },
] as const;

// ============================================================================
// localStorage Configuration
// ============================================================================

export const STORAGE_PREFIX = 'xivdyetools';

export const STORAGE_KEYS = {
  THEME: `${STORAGE_PREFIX}_theme`,
  LOCALE: `${STORAGE_PREFIX}_locale`,
  DARK_MODE: `${STORAGE_PREFIX}_dark_mode`,
  DUAL_DYES: `${STORAGE_PREFIX}_dual_dyes`,
  SHOW_PRICES: `${STORAGE_PREFIX}_show_prices`,
  LAST_TOOL: `${STORAGE_PREFIX}_last_tool`,
  PRICE_CACHE: `${STORAGE_PREFIX}_price_cache`,
  SAVED_PALETTES: `${STORAGE_PREFIX}_saved_palettes`,
  HARMONY_FILTERS: `${STORAGE_PREFIX}_harmony_filters`,
  HARMONY_SUGGESTIONS_MODE: `${STORAGE_PREFIX}_harmony_suggestions_mode`,
  HARMONY_COMPANION_DYES: `${STORAGE_PREFIX}_harmony_companion_dyes`,
  // Phase 2: Discoverability
  WELCOME_SEEN: `${STORAGE_PREFIX}_welcome_seen`,
  PALETTE_HINT_SEEN: `${STORAGE_PREFIX}_palette_hint_seen`,
  LAST_VERSION_VIEWED: `${STORAGE_PREFIX}_last_version_viewed`,
  // Phase 2.2: Collections & Favorites
  FAVORITES: `${STORAGE_PREFIX}_favorites`,
  COLLECTIONS: `${STORAGE_PREFIX}_collections`,
  // Phase 2: Tutorials
  TUTORIALS_DISABLED: `${STORAGE_PREFIX}_tutorials_disabled`,
} as const;

// ============================================================================
// UI Configuration
// ============================================================================

/**
 * Companion dyes configuration for Expanded Suggestions mode
 */
export const COMPANION_DYES_MIN = 1;
/** 1–5 per the drawn 1A Advanced panel (companion alternates ride the cards). */
export const COMPANION_DYES_MAX = 5;
export const COMPANION_DYES_DEFAULT = 1;

/**
 * Largest image the Palette Extractor accepts (dropped, pasted, picked or
 * from the camera). One cap so the drop/paste path cannot drift from the
 * upload-display path again (2026-08-21 security audit, WEB-13): decoding a
 * multi-GB image hangs the tab. `errors.imageTooLarge` quotes the same 20 MB.
 *
 * `.chara` files have their own, far smaller cap, core's
 * `MAX_CHARA_FILE_BYTES` (8 KiB since the 2026-10-15 import review). The
 * settings and collection imports read their `.json` files without a cap.
 */
export const MAX_USER_FILE_BYTES = 20 * 1024 * 1024;
